/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useModal } from '../contexts/ModalContext'
import { useServiceContext } from '../root'
import { toast } from '../services/toast'
import type { Workflow } from '../screens/workflows/types'
import WorkflowCancelModal from '../components/modals/WorkflowCancelModal'

/**
 * React Hook for Workflow Operations
 *
 * Provides functions for workflow status operations (suspend, resume, cancel)
 * following the patterns established in the rcm-new application.
 * Matches legacy useWorkflowsService from lego/page/workflows/
 */
export function useWorkflowsService() {
  const queryClient = useQueryClient()
  const { showModal } = useModal()
  const { service } = useServiceContext()

  /**
   * Get human-readable workflow type text
   * Matches legacy getReadableType from lego/page/workflows/useWorkflowsService.jsx
   *
   * @param type - Workflow type constant (e.g., 'NEW', 'CHANGE', 'OBSOLETE')
   * @returns Translated workflow type text
   */
  const getReadableType = (type: string): string => {
    const translate = service.filter.translate

    switch (type) {
      case 'FILE_ARTIFACT':
        return translate('WORKFLOWS_TYPE_READABLE_FILE_ARTIFACT')
      case 'RECLASSIFY_DOCUMENT':
        return translate('WORKFLOWS_TYPE_READABLE_RECLASSIFY_DOCUMENT')
      case 'CHANGE':
        return translate('BROWSER_ACTION_REQUEST_DOCUMENT_CHANGE_DOCUMENT')
      case 'NEW':
        return translate('WORKFLOW_DETAIL_ADD_DOCUMENT')
      case 'OBSOLETE':
        return translate('BROWSER_ACTION_REQUEST_DOCUMENT_DELETE_DOCUMENT')
      case 'OBSOLETE_REFERENCE':
        return translate('BROWSER_ACTION_REQUEST_DOCUMENT_DELETE_REFERENCE')
      case 'OBSOLETE_PLACEHOLDER':
        return translate('BROWSER_ACTION_REQUEST_DOCUMENT_DELETE_PLACEHOLDER')
      case 'TRAINING':
        return translate('TRAINING_TITLE')
      case 'IMPORT':
        return translate('DOCUMENT_MIGRATION_TITLE')
      case 'FILL_PLACEHOLDER':
        return translate('BROWSER_ACTION_REQUEST_DOCUMENT_CHANGE_PLACEHOLDER')
      case 'CREATE_PLACEHOLDER':
        return translate('BROWSER_ADD_PLACEHOLDER_H_TITLE')
      case 'CREATE_REFERENCE':
        return translate('BROWSER_ADD_ALIAS_H_TITLE')
      case 'EOS':
        return translate('WORKFLOWS_TYPE_EOS_LEGACY')
      case 'EOS_DOWNLOAD_AND_ACKNOWLEDGE':
        return translate('WORKFLOWS_TYPE_EOS_DOWNLOAD_AND_ACKNOWLEDGE')
      case 'QC_REVIEW':
        return translate('QC_REVIEW')
      default:
        return translate('NOT_AVAILABLE')
    }
  }

  /**
   * Changes workflow status via API
   * Internal helper function
   */
  const changeWorkflowStatus = async (workflowId: number, status: string, validation?: any): Promise<void> => {
    // TODO: Implement changeWorkflowStatus in workflows API
    // Use legacy API structure that matches AngularJS implementation
    // This calls the PATCH endpoint: /api/workflows/{workflowId}/{status}
    // return api.workflows.changeWorkflowStatus(workflowId, status, validation)
    console.warn('changeWorkflowStatus not yet implemented in workflows API')
    return Promise.resolve()
  }

  /**
   * Generic workflow status change handler
   * Reduces duplication between suspend/resume operations
   */
  const executeWorkflowStatusChange = async (
    workflowId: number,
    status: 'suspend' | 'resume',
    onSuccess?: () => void
  ): Promise<void> => {
    return new Promise((resolve) => {
      const callApi = async (validation?: any) => {
        try {
          await changeWorkflowStatus(workflowId, status, validation)

          if (onSuccess) {
            onSuccess()
          }

          // Invalidate queries to refresh data
          queryClient.invalidateQueries({ queryKey: ['workflows'] })
          queryClient.invalidateQueries({ queryKey: ['tasks'] })

          const messageKey = status === 'suspend' ? 'WORKFLOW_SUSPENDED' : 'WORKFLOW_RESUMED'
          toast.success(messageKey)
          resolve()
        } catch (error: any) {
          const action = status === 'suspend' ? 'suspend' : 'resume'
          toast.error(error?.data?.message || `Failed to ${action} workflow`)
        }
      }

      // Note: E-signature modal integration will be added when modal service is available
      // For now, call API directly
      callApi()
    })
  }

  /**
   * Suspends a workflow
   * Matches legacy suspend operation
   *
   * @param workflowId - ID of the workflow to suspend
   * @param onSuccess - Optional callback to execute on success
   * @returns Promise that resolves when operation completes
   */
  const suspend = async (workflowId: number, onSuccess?: () => void): Promise<void> => {
    return executeWorkflowStatusChange(workflowId, 'suspend', onSuccess)
  }

  /**
   * Resumes a suspended workflow
   * Matches legacy resume operation
   *
   * @param workflowId - ID of the workflow to resume
   * @param onSuccess - Optional callback to execute on success
   * @returns Promise that resolves when operation completes
   */
  const resume = async (workflowId: number, onSuccess?: () => void): Promise<void> => {
    return executeWorkflowStatusChange(workflowId, 'resume', onSuccess)
  }

  /**
   * Cancels one or more workflows
   * Matches legacy cancel operation - opens modal for comment entry
   *
   * @param workflowIds - Array of workflow IDs to cancel
   * @param workflow - Workflow object for context (used for displaying workflow number)
   * @param onSuccess - Optional callback to execute on success
   * @param keepParentModalsOpened - Whether to keep parent modals open
   * @returns Promise that resolves when operation completes
   */
  const cancel = async (
    workflowIds: number[],
    workflow: Workflow,
    onSuccess?: () => void,
    keepParentModalsOpened?: boolean
  ): Promise<void> => {
    return new Promise((resolve) => {
      const handleSuccess = () => {
        // Invalidate queries to refresh data
        queryClient.invalidateQueries({ queryKey: ['workflows'] })
        queryClient.invalidateQueries({ queryKey: ['tasks'] })

        if (onSuccess) {
          onSuccess()
        }
        resolve()
      }

      // Show cancel modal with comment field and e-signature support
      showModal(
        <WorkflowCancelModal
          workflowIds={workflowIds}
          workflowNo={workflow.formattedId}
          onSuccess={handleSuccess}
          isEosFlow={workflow.workflowType === 'EOS_DOWNLOAD_AND_ACKNOWLEDGE'}
        />,
        { keepParentModalsOpen: keepParentModalsOpened }
      )
    })
  }

  return {
    suspend,
    resume,
    cancel,
    getReadableType
  }
}

export default useWorkflowsService
